import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Switch,
  TextField,
  IconButton,
  Button,
  makeStyles,
  Typography,
  CircularProgress,
  Grid,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import StyledButton from "../../../common/models/Button";
import axios from "axios";
import SaveIcon from "@material-ui/icons/Save";
import CancelIcon from "@material-ui/icons/Cancel";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    padding: theme.spacing(2),
  },
  table: {
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    width: "650px",
  },
  headerCell: {
    fontWeight: 600,
    backgroundColor: "#f0f0f0",
    padding: "10px 16px",
    textAlign: "left",
  },
  bodyCell: {
    padding: "10px 16px",
    textAlign: "left",
    verticalAlign: "middle",
  },
  textField: {
    "& input": {
      padding: "6px 8px",
      height: "30px",
    },
  },
  inlineActions: {
    display: "flex",
    flexDirection: "column",
    marginLeft: "8px",
    gap: "4px",
  },
  iconBtn: {
    padding: "1px",
  },
  addButton: {
    marginTop: theme.spacing(2),
    backgroundColor: "#fff",
    color: "#000",
    borderRadius: "20px",
    padding: "6px 16px",
    textTransform: "none",
    fontWeight: 600,
    border: "1px solid #000",
    display: "flex",
    alignItems: "center",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
  addIconCircle: {
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    border: "2px solid #000",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: theme.spacing(1),
    fontSize: "16px",
    fontWeight: 600,
  },
  deleteIcon: {
    color: theme.palette.error.main,
  },
  requiredAsterisk: {
    color: theme.palette.error.main,
  },
  switchBase: {
    "&.Mui-checked": {
      color: "#007bff",
    },
    "&.Mui-checked + .MuiSwitch-track": {
      backgroundColor: "#007bff",
    },
  },
  confidenceFieldWrapper: {
    marginTop: theme.spacing(1),
    width: "100%",
    maxWidth: "1200px",
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    maxWidth: "900px",
    marginTop: theme.spacing(4),
  },
  confidenceLabel: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  backButton: {
    backgroundColor: "#555",
    color: "#fff",
    borderRadius: "20px",
    padding: "8px 24px",
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#333",
    },
  },
  saveButton: {
    backgroundColor: "#f26522",
    color: "#fff",
    borderRadius: "20px",
    padding: "8px 24px",
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#d4551a",
    },
  },
}));

const DigitalizationCodesTab = (props) => {
  const { addSponsorList, rowData, rowNetwork, updateNetworkData } = props;
  const classes = useStyles();

  const [digitalizationCodes, setDigitalizationCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [savingStates, setSavingStates] = useState({});
  const [isSaving, setIsSaving] = useState(false); // Track save & next button loading
  const [lowConfidenceLevel, setLowConfidenceLevel] = useState("");
  const [mediumConfidenceLevel, setMediumConfidenceLevel] = useState({
    min: "",
    max: "",
  });
  const [highConfidenceLevel, setHighConfidenceLevel] = useState("");
  const [updatingConfidence, setUpdatingConfidence] = useState(false);
  const [originalOrgSettings, setOriginalOrgSettings] = useState(null);

  const network = addSponsorList?.data?.network || rowNetwork;
  const networkUrl = updateNetworkData?.ecosystemUrl || rowData?.networkURL;

  const fetchDigitalizationCodes = async () => {
    setError(null);
    setLoading(true);

    try {
      const url = `${networkUrl}/Pharma%20Contract%20Manufacturing/${network}/Pharma%20Co/Admin/fetchDigitalizationCodes`;
      const response = await axios.get(url);
      const data = response?.data;

      if (data?.status === true || data?.status === "true") {
        const list = data.digitalizationCodesList || [];
        const digitalizationCodesList = list.map((item, index) => ({
          id: item.digitalizationCodeID || `ID-${index + 1}`,
          code: item.digitalizationCode,
          description: item.digitalizationCodeDescription || "",
          selected: !!item.activated,
          isNew: false,
          type: item.digitalizationCodeType,
        }));
        setDigitalizationCodes(digitalizationCodesList);
      } else {
        setError("Something went wrong. Please try again.");
      }
    } catch (err) {
      console.error("API Error:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchOrgLevelSettings = async () => {
      try {
        const url = `${networkUrl}/Pharma%20Contract%20Manufacturing/${network}/Pharma%20Co/Admin/fetchOrgLevelSettings`;
        const response = await axios.get(url);
        console.log(response);
        if (response.data?.orgLevelSettings) {
          const settings = response.data.orgLevelSettings;

          setLowConfidenceLevel(settings.lowConfidenceLimit || "");
          setMediumConfidenceLevel({
            min: settings.mediumConfidenceLimitMinValue || "",
            max: settings.mediumConfidenceLimitMaxValue || "",
          });
          setHighConfidenceLevel(settings.highConfidenceLimit || "");

          // store the original values for comparison later
          setOriginalOrgSettings({
            id: settings.id,
            low: settings.lowConfidenceLimit || "",
            medMin: settings.mediumConfidenceLimitMinValue || "",
            medMax: settings.mediumConfidenceLimitMaxValue || "",
            high: settings.highConfidenceLimit || ""
          });
        }
      } catch (error) {
        console.error("Error fetching Org Level Settings:", error);
      }
    };

  useEffect(() => {
    fetchDigitalizationCodes();
    fetchOrgLevelSettings();
  }, []);

  const saveDigitalizationCode = async (index) => {
    if (savingStates[index]) return;
    setSavingStates((prev) => ({ ...prev, [index]: true }));
    try {
      const digitalizationCodeToSave = digitalizationCodes[index];
      if (!digitalizationCodeToSave.code.trim()) {
        setError("Please provide the Digitalization Code.");
        setSavingStates((prev) => ({ ...prev, [index]: false }));
        return;
      }

      const payload = {
        ecosystemId: addSponsorList?.data?.ecosystemId || rowData?.ecosystemId,
        sponsorId: addSponsorList?.data?.organizationId || rowData?.organizationId,
        digitalizationCodesList: [
          {
            digitalizationCode: digitalizationCodeToSave.code.trim(),
            digitalizationCodeDescription: digitalizationCodeToSave.description,
            digitalizationCodeType: "New",
            activated: digitalizationCodeToSave.selected,
          },
        ],
      };

      const url = `${networkUrl}/Pharma%20Contract%20Manufacturing/${network}/Pharma%20Co/Admin/addDigitalizationCodes`;
      const response = await axios.post(url, payload);

      if (response.data?.status === true || response.data?.status === "true") {
        fetchDigitalizationCodes();
        props.setDigitalizationCode((prev) => !prev);
        setDigitalizationCodes((prev) =>
          prev.map((format, i) =>
            i === index ? { ...format, id: response.data?.id, isNew: false } : format
          )
        );
      } else {
        console.error("Failed to save Digitalization Code", response.data?.message);
        setError(response.data?.message || "Failed to save Digitalization Code.");
      }
    } catch (error) {
      console.error("Error saving Digitalization Code:", error);
      setError("An error occurred while saving the Digitalization Code.");
    }
    setSavingStates((prev) => ({ ...prev, [index]: false }));
  };

  const handleDelete = async (index) => {
    try {
      const digitalizationCodeToDelete = digitalizationCodes[index];
      if (digitalizationCodeToDelete.isNew) {
        setDigitalizationCodes((prev) => prev.filter((_, i) => i !== index));
        return;
      }

      const payload = {
        ecosystemId: addSponsorList?.data?.ecosystemId || rowData?.ecosystemId,
        sponsorId: addSponsorList?.data?.organizationId || rowData?.organizationId,
        digitalizationCodesList: [
          {
            digitalizationCodeID: digitalizationCodeToDelete.id,
            digitalizationCode: digitalizationCodeToDelete.code,
            activated: digitalizationCodeToDelete.selected,
            markForDelete: true,
          },
        ],
      };

      const url = `${networkUrl}/Pharma%20Contract%20Manufacturing/${network}/Pharma%20Co/Admin/updateDigitalizationCodes`;
      const response = await axios.post(url, payload);
      fetchDigitalizationCodes();

      if (response.data?.status === true || response.data?.status === "true") {
        props.setDigitalizationCode((prev) => !prev);
        setDigitalizationCodes((prev) => prev.filter((_, i) => i !== index));
      } else {
        console.error("Failed to delete digitalization code:", response.data?.message);
        setError(response.data?.message || "Failed to delete digitalization code.");
      }
    } catch (error) {
      console.error("Error deleting digitalization code:", error);
      setError("An error occurred while deleting the digitalization code.");
    }
  };

  const handleNextClick = async () => {
    setErrorMessage("");
    setUpdatingConfidence(false);
    try {
      if (
        lowConfidenceLevel === "" ||
        mediumConfidenceLevel.min === "" ||
        mediumConfidenceLevel.max === "" ||
        highConfidenceLevel === ""
      ) {
        setErrorMessage("Please fill all confidence level values.");
        return;
      }

      const low = Number((lowConfidenceLevel ?? "").toString().trim());
      const medMin = Number((mediumConfidenceLevel.min ?? "").toString().trim());
      const medMax = Number((mediumConfidenceLevel.max ?? "").toString().trim());
      const high = Number((highConfidenceLevel ?? "").toString().trim());

      if ([low, medMin, medMax, high].some((v) => isNaN(v))) {
        setErrorMessage("Confidence level values must be numbers only.");
        return;
      }

      if (low >= high) {
        setErrorMessage("Low Confidence must be less than high confidence level");
        return;
      }
      if (medMin >= medMax) {
        setErrorMessage("Medium Min confidence level must be less than Medium Max confidence value.");
        return;
      }

      setErrorMessage("");

      setIsSaving(true); // Start loading

      const updatedCodes = digitalizationCodes.filter((dc) => dc.isUpdated);
      if (updatedCodes.length) {
        if (digitalizationCodes.every((dc) => !dc.selected)) {
          setErrorMessage("Please enable at least one digitalization code.");
          setUpdatingConfidence(false);
          return;
        }

        const codesPayload = {
          ecosystemId: addSponsorList?.data?.ecosystemId || rowData?.ecosystemId,
          sponsorId: addSponsorList?.data?.organizationId || rowData?.organizationId,
          digitalizationCodesList: updatedCodes.map((dc) => ({
            digitalizationCodeID: dc.id,
            digitalizationCode: dc.code,
            digitalizationCodeDescription: dc.description,
            activated: dc.selected,
          })),
        };

        const updateCodesUrl = `${networkUrl}/Pharma%20Contract%20Manufacturing/${network}/Pharma%20Co/Admin/updateDigitalizationCodes`;
        const updateCodesResp = await axios.post(updateCodesUrl, codesPayload);

        if (!(updateCodesResp.data?.status === true || updateCodesResp.data?.status === "true")) {
          setError(updateCodesResp.data?.message || "Failed to update digitalization codes.");
          setUpdatingConfidence(false);
          return;
        }

        await fetchDigitalizationCodes();
      }

      if (originalOrgSettings?.id) {
        const hasChanges =
          String(lowConfidenceLevel) !== String(originalOrgSettings.low) ||
          String(mediumConfidenceLevel.min) !== String(originalOrgSettings.medMin) ||
          String(mediumConfidenceLevel.max) !== String(originalOrgSettings.medMax) ||
          String(highConfidenceLevel) !== String(originalOrgSettings.high);

        if (hasChanges) {
          setUpdatingConfidence(true);
          const updatePayload = {
            id: originalOrgSettings.id,
            lowConfidenceLimit: low,
            mediumConfidenceLimitMinValue: medMin,
            mediumConfidenceLimitMaxValue: medMax,
            highConfidenceLimit: high,
            organization_id: addSponsorList?.data?.organizationId || rowData?.organizationId,
            sponsorId: addSponsorList?.data?.organizationId || rowData?.organizationId,
            ecosystem_id: addSponsorList?.data?.ecosystemId || rowData?.ecosystemId,
          };

          const updateUrl = `${networkUrl}/Pharma%20Contract%20Manufacturing/${network}/Pharma%20Co/Admin/updateOrgLevelSettings`;
          const updateResp = await axios.post(updateUrl, updatePayload);

          if (!(updateResp.data?.status === true || updateResp.data?.status === "true")) {
            setErrorMessage(updateResp.data?.message || "Failed to update Org Level Settings.");
            setUpdatingConfidence(false);
            return;
          }
        }
      }

      setUpdatingConfidence(false);
      props.sponsorHandleNext();

    } catch (error) {
      console.error("Error in Save & Next:", error);
      setError("Something went wrong during Save & Next.");
      setUpdatingConfidence(false);
    } finally {
      setIsSaving(false); // Stop loading
    }
  };

  const handleToggle = (index) => {
    const updated = [...digitalizationCodes];
    updated[index].selected = !updated[index].selected;
    updated[index].isUpdated = true;
    setDigitalizationCodes(updated);
  };

  const handleDigitalizationCodeChange = (index, value) => {
    const updated = [...digitalizationCodes];
    updated[index].code = value;
    setDigitalizationCodes(updated);
  };

  const handleDigitalizationDescriptionChange = (index, value) => {
    const updated = [...digitalizationCodes];
    updated[index].description = value;
    setDigitalizationCodes(updated);
  };

  const handleAddDigitalizationCode = () => {
    setDigitalizationCodes([
      ...digitalizationCodes,
      {
        code: "",
        description: "",
        selected: false,
        isNew: true,
      },
    ]);
  };

  const handleCancel = (index) => {
    const updated = digitalizationCodes.filter((_, i) => i !== index);
    setDigitalizationCodes(updated);
  };

  return (
    <div className={classes.container}>
      <div>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell className={classes.headerCell}>Selection</TableCell>
              <TableCell className={classes.headerCell}>Digitalization Code</TableCell>
              <TableCell className={classes.headerCell}>Digitalization Code Description</TableCell>
              <TableCell className={classes.headerCell}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100px",
                    }}
                  >
                    <CircularProgress />
                  </div>
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "60px",
                      backgroundColor: "#ffecec",
                      color: "#b00020",
                      border: "1px solid #ff9999",
                    }}
                  >
                    <Typography>{error}</Typography>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              digitalizationCodes.map((item, index) => (
                <TableRow key={item.id}>
                  <TableCell className={classes.bodyCell}>
                    <Switch
                      checked={item.selected}
                      onChange={() => handleToggle(index)}
                      classes={{ switchBase: classes.switchBase }}
                    />
                  </TableCell>
                  <TableCell className={classes.bodyCell}>
                    {item.isNew ? (
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <TextField
                          placeholder="Type Code"
                          value={item.code}
                          onChange={(e) => handleDigitalizationCodeChange(index, e.target.value)}
                          className={classes.textField}
                          variant="outlined"
                          inputProps={{
                            onBeforeInput: (e) => {
                              const allowedChars = /^[a-zA-Z0-9.]+$/;
                              if (!allowedChars.test(e.data)) {
                                e.preventDefault();
                              }
                            },
                          }}
                        />
                      </div>
                    ) : (
                      item.code
                    )}
                  </TableCell>
                  <TableCell className={classes.bodyCell}>
                    {item.isNew ? (
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <TextField
                          placeholder="Type Description"
                          value={item.description}
                          onChange={(e) => handleDigitalizationDescriptionChange(index, e.target.value)}
                          className={classes.textField}
                          variant="outlined"
                          inputProps={{
                            onBeforeInput: (e) => {
                              const allowedChars = /^[a-zA-Z. ]+$/;
                              if (!allowedChars.test(e.data)) {
                                e.preventDefault();
                              }
                            },
                          }}
                        />
                        <div className={classes.inlineActions}>
                          <IconButton
                            onClick={() => handleCancel(index)}
                            className={classes.iconBtn}
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            onClick={() => saveDigitalizationCode(index)}
                            className={classes.iconBtn}
                            disabled={
                              !item.code?.trim() ||
                              !item.description?.trim() ||
                              digitalizationCodes.some(
                                (f, i) =>
                                  i !== index &&
                                  f.code?.trim().toLowerCase() === item.code?.trim().toLowerCase()
                              ) ||
                              savingStates[index]
                            }
                          >
                            <SaveIcon fontSize="small" />
                          </IconButton>
                        </div>
                      </div>
                    ) : (
                      item.description
                    )}
                  </TableCell>
                  <TableCell className={classes.bodyCell}>
                    {(item.isNew === true || item.type === "New") && (
                      <IconButton aria-label="delete" onClick={() => handleDelete(index)}>
                        <DeleteIcon className={classes.deleteIcon} />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      
        <Button
          variant="outlined"
          onClick={handleAddDigitalizationCode}
          className={classes.addButton}
        >
          <span className={classes.addIconCircle}>+</span>
          Add New Digitalization Code
        </Button>
      </div>

      <div style={{ width: "100%", maxWidth: "1250px", marginTop: 12 }}>
       {errorMessage && (
          <div style={{ color: "red", marginTop: 10 }} className="error-message">
            {errorMessage}
          </div>
        )}
        <Grid container spacing={4} className={classes.confidenceFieldWrapper}>
          {/* Low Confidence */}
          <Grid item xs={12} sm={6}>
            <Typography className={classes.confidenceLabel}>
              Low Confidence Level (&lt;/=) % <span className={classes.requiredAsterisk}>*</span>
            </Typography>
            <TextField
              style={{ width: "380px" }}
              value={lowConfidenceLevel}
              onChange={(e) => setLowConfidenceLevel(e.target.value)}
              variant="outlined"
              className={classes.textField}
              placeholder="Enter Low Confidence"
              inputProps={{
                onBeforeInput: (e) => {
                  if (!/^[0-9.]$/.test(e.data)) e.preventDefault();
                },
              }}
            />
          </Grid>

          {/* Medium Confidence */}
          <Grid item xs={12} sm={6}>
            <Typography className={classes.confidenceLabel}>
              Medium Confidence Level (between) % {" "}
              <span className={classes.requiredAsterisk}>*</span>
            </Typography>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <TextField
                value={mediumConfidenceLevel.min || ""}
                onChange={(e) =>
                  setMediumConfidenceLevel((prev) => ({ ...prev, min: e.target.value }))
                }
                variant="outlined"
                placeholder="Min"
                inputProps={{
                  onBeforeInput: (e) => {
                    if (!/^[0-9.]$/.test(e.data)) e.preventDefault();
                  },
                }}
                className={classes.textField}
                style={{ width: "130px" }}
              />
              <Typography variant="body1" style={{ whiteSpace: "nowrap" }}>
                to
              </Typography>
              <TextField
                value={mediumConfidenceLevel.max || ""}
                onChange={(e) =>
                  setMediumConfidenceLevel((prev) => ({ ...prev, max: e.target.value }))
                }
                variant="outlined"
                placeholder="Max"
                inputProps={{
                  onBeforeInput: (e) => {
                    if (!/^[0-9.]$/.test(e.data)) e.preventDefault();
                  },
                }}
                className={classes.textField}
                style={{ width: "130px" }}
              />
            </div>
          </Grid>

          {/* High Confidence */}
          <Grid item xs={12} sm={6}>
            <Typography className={classes.confidenceLabel}>
              High Confidence Level (&gt;/=) % <span className={classes.requiredAsterisk}>*</span>
            </Typography>
            <TextField
              style={{ width: "380px" }}
              value={highConfidenceLevel}
              onChange={(e) => setHighConfidenceLevel(e.target.value)}
              variant="outlined"
              className={classes.textField}
              placeholder="Enter High Confidence"
              inputProps={{
                onBeforeInput: (e) => {
                  if (!/^[0-9.]$/.test(e.data)) e.preventDefault();
                },
              }}
            />
          </Grid>
        </Grid>
        {updatingConfidence && (
          <div style={{ marginTop: 10 }}>
            <CircularProgress size={20} /> Updating...
          </div>
        )}
      </div>

      <div className={classes.footer}>
        <Button className={classes.backButton} onClick={() => props.handleBack()}>
          &lt; Back
        </Button>

        <StyledButton 
          className={classes.saveButton} 
          onClick={handleNextClick}
          style={{ 
            opacity: isSaving ? 0.7 : 1, 
            pointerEvents: isSaving ? 'none' : 'auto' 
          }}
        >
          {isSaving ? (
            <>
              <CircularProgress size={16} color="inherit" style={{ marginRight: '8px' }} />
              Saving...
            </>
          ) : (
            'Save & Next'
          )}
        </StyledButton>
      </div>
    </div>
  );
};

export default DigitalizationCodesTab;
